/**
 * useDisplayWalks - where each walking figure is now on this screen (plan design-gaps, M3).
 *
 * Each move the shell heard of (useShellWalks) becomes a walk (engine/display/walk) timed from the moment THIS
 * screen saw it, as the beats are; the display clock runs only while one plays. Returns each walking figure's
 * point by id, each walk with the clock's time it is drawn at (a crowd's bodies trail it, M4), and the newest
 * walker, for the camera to follow. A screen that holds still gets none: its figures stand at their destinations.
 * The move's line reaches every screen ahead of the token's write (useShellPings flushes it), so the walk is in
 * hand before the figure's committed position moves.
 *
 * @param {Record<string, import('../engine/moveBeat').Move & { seenAt: number }> | undefined} moves - by actor
 * @param {boolean} still - the viewer's motion setting: no animation
 * @returns {{ positions: Record<string, import('../engine/display/walk').WalkPoint>,
 *   playing: Record<string, { walk: import('../engine/display/walk').Walk, t: number }>,
 *   walking: { id: string, x: number, z: number, y: number } | null }}
 */
import { useEffect, useMemo, useState } from 'react';
import { walkAt, walkOf } from '../engine/display/walk';
import { noteSettled } from '../diagnostics/beatLog';
import useDisplayClock from './useDisplayClock';

const NONE = Object.freeze({ positions: Object.freeze({}), playing: Object.freeze({}), walking: null });

export default function useDisplayWalks(moves, still) {
  const walks = useMemo(() => Object.values(moves ?? {}).flatMap((m) => walkOf(m, m.seenAt) ?? []), [moves]);
  const [now, setNow] = useState(() => Date.now());
  // The fuzzer's walk oracle reads which moves this screen settled (engine/fuzz/beats): every one it is handed,
  // walked or (holding still) stood at the end of.
  useEffect(() => {
    for (const m of Object.values(moves ?? {})) noteSettled(m.id);
  }, [moves]);
  const playing = !still && walks.some((w) => now < w.at + w.ms);
  useDisplayClock(playing, () => setNow(Date.now()));
  return useMemo(() => (still ? NONE : walkingNow(walks, now)), [walks, now, still]);
}

/** Each walk still playing at `now`: its figure's point and its walk, and the newest walker's. */
function walkingNow(walks, now) {
  const positions = {};
  const playing = {};
  for (const w of walks) {
    // A walk seen after the clock's last frame starts now, not "not yet".
    const t = Math.max(now, w.at);
    if (t - w.at >= w.ms) continue;
    positions[w.combatantId] = walkAt(w, t);
    playing[w.combatantId] = { walk: w, t };
  }
  const newest = newestOf(walks.filter((w) => w.combatantId in positions));
  if (!newest) return NONE;
  return { positions, playing, walking: { id: newest.combatantId, ...positions[newest.combatantId] } };
}

const newestOf = (walks) => walks.reduce((best, w) => (!best || w.at > best.at ? w : best), null);
